import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Tab,
  Tabs,
  TabProps,
  TextField,
  Button,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@mui/material/styles';
import { Patient } from '../services/profile';
import { localizeAge } from '../component/PatientProfile';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ProfileSetupContext } from './ProfileSetupContext';
import {
  BackButton,
  LoadingIndicator,
  Routes,
} from '@xolv-mybrightlink/common';

type PatientTabProps = { patient: Patient; active: boolean } & TabProps;

const PatientTab = (props: PatientTabProps) => {
  const { patient, active, ...rest } = props;
  const intl = useIntl();

  const ageInLocalize = useMemo(() => {
    if (!patient.dateOfBirth) return '';
    return localizeAge(patient.dateOfBirth, new Date(), intl);
  }, [patient.dateOfBirth, intl]);

  const textStyle = {
    fontWeight: 'normal',
    color: active ? '#fff' : '#626a7a',
  };

  return (
    <Tab
      key={`profile-tab-${patient.guid}`}
      sx={{ px: 15, py: 2.5, ...(active ? tabActiveStyle : {}) }}
      label={
        <>
          <Typography
            sx={{
              color: active ? '#fff' : '#3b4559',
              fontWeight: active ? 600 : 'normal',
            }}
          >
            {patient.lastName}
          </Typography>
          <Box sx={{ display: 'flex' }}>
            <Typography variant='subtitle2' sx={textStyle}>
              {ageInLocalize}
            </Typography>
            {(patient.gender === 'M' || patient.gender === 'F') && (
              <Typography variant='subtitle2' sx={textStyle}>
                {', '}
                <FormattedMessage
                  id={`profile.children.${
                    patient.gender === 'M' ? 'male' : 'female'
                  }`}
                />
              </Typography>
            )}
          </Box>
        </>
      }
      {...rest}
    />
  );
};

type QuestionMap = Record<string, Record<string, boolean>>;

const ProfileButton = styled(Button)({
  '& .MuiButton-root': {
    mt: 3,
    mb: 4,
    flex: 1,
    pt: 3.5,
    pb: 3.5,
    borderRadius: '30px',
  },
});

const ProfileTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    backgroundColor: '#fff',
    borderRadius: '12px',
  },
  '& fieldset': {
    borderRadius: '12px',
  },
});

const toBooleanMap = (
  list: string[],
  activeItems: string,
): Record<string, boolean> => {
  return list.reduce(
    (acc: Record<string, boolean>, curr: string) =>
      Object.assign(acc, { [curr]: curr === activeItems }),
    {},
  );
};

const genQuestion = (behaviorData: Record<string, string>): QuestionMap => {
  return {
    'communication-level': toBooleanMap(
      ['good', 'not-talk-much', 'use-device', 'not-talk'],
      behaviorData['communication-level'],
    ),
    'interaction-level': toBooleanMap(
      [
        'engage-regularly',
        'engage-in-structured-environments',
        'be-by-themselves',
      ],
      behaviorData['interaction-level'],
    ),
    'self-care-assistance-level': toBooleanMap(
      [
        'completely-independent',
        'need-some-assistance',
        'need-full-assistance',
      ],
      behaviorData['self-care-assistance-level'],
    ),
  };
};

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => <Tabs {...props} />)({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-scroller': {
    padding: '10px',
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  dir?: string;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return <Box {...other}>{value === index && <Box>{children}</Box>}</Box>;
};

const tabActiveStyle = {
  backgroundColor: '#307fe2',
  borderRadius: 8,
  boxShadow:
    '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
};

export const ProfileSetupBehaviors = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState(false);

  const { profile, updateProfile, submitProfile } =
    useOutletContext<ProfileSetupContext>();
  const patients = profile?.patients ?? [];

  const {
    strengthsByPatientGuid,
    behaviorsByPatientGuid,
    questionsByPatientGuid,
  } = patients.reduce(
    (acc, patient) => {
      const {
        strengthsByPatientGuid,
        behaviorsByPatientGuid,
        questionsByPatientGuid,
      } = acc;
      const behaviors = {
        'communication-level': patient.communicationLevel,
        'interaction-level': patient.interactionLevel,
        'self-care-assistance-level': patient.selfCareAssistanceLevel,
      };
      strengthsByPatientGuid.set(patient.guid, patient.strengths ?? '');
      behaviorsByPatientGuid.set(patient.guid, behaviors);
      questionsByPatientGuid.set(patient.guid, genQuestion(behaviors));
      return acc;
    },
    {
      strengthsByPatientGuid: new Map<string, string>(),
      behaviorsByPatientGuid: new Map<string, Record<string, string>>(),
      questionsByPatientGuid: new Map<string, QuestionMap>(),
    },
  );

  const updateBehaviors = useCallback(
    (patientGuid: string, questionKey: string, answerKey: string) => {
      updateProfile('patients', (profile) => {
        const patients = profile?.patients ?? [];
        return patients.map((patient) => {
          if (patient.guid !== patientGuid) {
            return patient;
          }

          const behaviors = behaviorsByPatientGuid.get(patient.guid) ?? {};
          const answer = (question: string) => {
            return question === questionKey
              ? answerKey
              : behaviors[question] ?? '';
          };
          return {
            ...patient,
            communicationLevel: answer('communication-level'),
            interactionLevel: answer('interaction-level'),
            selfCareAssistanceLevel: answer('self-care-assistance-level'),
          };
        });
      });
    },
    [updateProfile, behaviorsByPatientGuid],
  );

  const updateStrengths = useCallback(
    (patientGuid: string, strengths: string) => {
      updateProfile('patients', (profile) => {
        const patients = profile?.patients ?? [];
        return patients.map((patient) => {
          if (patient.guid !== patientGuid) {
            return patient;
          }
          return {
            ...patient,
            strengths,
          };
        });
      });
    },
    [updateProfile],
  );

  const submitProfileChange = useCallback(async () => {
    setLoading(true);
    await submitProfile();
    setLoading(false);
    navigate(Routes.profile.index);
  }, [submitProfile, navigate]);

  const activeStyle = {
    border: '0.5px solid #d8dade',
    backgroundColor: '#307fe2',
  };

  const activeTextStyle = {
    color: '#fff',
    fontWeight: 500,
  };

  const renderQuestion = (patientGuid: string, questionKey: string) => {
    const choices = (questionsByPatientGuid.get(patientGuid) ?? {})[
      questionKey
    ];
    return Object.keys(choices).map((choiceKey: string) => {
      return (
        <Box
          key={choiceKey}
          sx={{
            width: 'fit',
            borderRadius: 22,
            maxWidth: '45%',
            boxShadow:
              '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
            backgroundColor: '#fff',
            px: 5,
            py: 4.5,
            cursor: 'pointer',
            mr: 5,
            mb: 5,
            height: 'fit-content',
            ...(choices[choiceKey] ? activeStyle : {}),
          }}
          onClick={() => updateBehaviors(patientGuid, questionKey, choiceKey)}
        >
          <Typography
            variant='body1'
            sx={{
              color: '#3b4559',
              cursor: 'pointer',
              userSelect: 'none',
              fontWeight: 'normal',
              ...(choices[choiceKey] ? activeTextStyle : {}),
            }}
          >
            <FormattedMessage
              id={`profile.children.question.${questionKey}.${choiceKey}`}
            />
          </Typography>
        </Box>
      );
    });
  };

  const renderTab = (patientGuid: string, index: number) => {
    const questions = questionsByPatientGuid.get(patientGuid);

    if (!questions) return null;

    return (
      <Box sx={{ my: 10 }}>
        {Object.keys(questions).map((questionKey: string) => {
          return (
            <Box key={`${index}-${questionKey}`} sx={{ mt: 10 }}>
              <Typography sx={{ fontWeight: 500, mb: 2 }}>
                <FormattedMessage
                  id={`profile.children.question.${questionKey}`}
                />
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                }}
              >
                {renderQuestion(patientGuid, questionKey)}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Typography variant='h6' sx={{ fontWeight: 600 }}>
        <FormattedMessage
          id='profile.setup.children.message-1'
          values={{ numChild: patients.length }}
        />
      </Typography>

      <Typography variant='body1'>
        <FormattedMessage id='profile.setup.children.message-2' />
      </Typography>

      <Box
        sx={{
          mt: 10,
          width: '100%',
          p: 0,
        }}
      >
        <Typography sx={{ fontWeight: 'bold' }}>
          <FormattedMessage id='profile.setup.label' />
        </Typography>
        <Box
          sx={{
            mt: 4,
            width: 'fit-content',
            gap: 8,
            borderRadius: 10,
            backgroundColor: '#fff',
          }}
        >
          <StyledTabs
            value={tabValue}
            onChange={(event, newValue) => {
              setTabValue(newValue);
            }}
          >
            {profile?.patients &&
              profile?.patients?.map((p: Patient, index: number) => {
                return (
                  <PatientTab
                    key={p.guid}
                    patient={p}
                    active={tabValue === index}
                  />
                );
              })}
          </StyledTabs>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography variant='subtitle2' sx={{ color: '#898f9b' }}>
            <FormattedMessage id='profile.setup.children.name.hint' />
          </Typography>
        </Box>

        {patients.map((patient, index: number) => {
          return (
            <TabPanel key={index} value={tabValue} index={index}>
              <Box sx={{ mt: 10 }}>
                <Typography sx={{ fontWeight: 500 }}>
                  <FormattedMessage id='profile.setup.children.question.strengths' />
                </Typography>
                <ProfileTextField
                  multiline
                  minRows={4}
                  maxRows={6}
                  sx={{
                    width: 0.6,
                    mt: 2.5,
                  }}
                  defaultValue={strengthsByPatientGuid.get(patient.guid) ?? ''}
                  onBlur={(event) =>
                    updateStrengths(patient.guid, event.target.value)
                  }
                />
              </Box>
              {renderTab(patient.guid, index)}
            </TabPanel>
          );
        })}
      </Box>

      {!loading && (
        <Box sx={{ mt: 20, mb: 10 }}>
          <Box sx={{ display: 'flex', gap: 8, width: 0.5, mx: 'auto' }}>
            <BackButton size='large' />
            <ProfileButton
              variant='contained'
              size='large'
              sx={{ borderRadius: '30px', px: 35, py: 4.5 }}
              disableElevation
              disableRipple={true}
              onClick={submitProfileChange}
            >
              <FormattedMessage id='actions.continue' />
            </ProfileButton>
          </Box>
        </Box>
      )}

      {loading && <LoadingIndicator />}
    </Box>
  );
};
